/**
 * AISafetyChat — Sprint D (Feb 2026)
 *
 * Client for `POST /api/ai-safety/chat` (SSE streaming).
 *
 * The empty state keeps the onboarding cards (5 sample questions +
 * 4 principles) from the spec. Once the user sends a first message,
 * the transcript takes over.
 *
 * Private-by-default: session id lives in `sessionStorage` (not
 * `localStorage`) so a tab close forgets it. A "Clear chat" button
 * deletes the server-side transcript in one tap.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sparkles, Send, Loader2, Lock, HandMetal, AlertCircle, GraduationCap,
  MessageCircle, Flag, HelpCircle, CalendarClock, ShieldHalf, Trash2,
  ShieldAlert, Crown, ChevronRight, Wand2, Heart, Type, Coffee,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/contexts/AuthContext';
import { LegalPageShell, HeroCard, Callout, LegalFooter } from '@/components/legal/LegalPrimitives';

// ============================================================
// MODES — Jun 2026 web-parity split (see mobile handoff).
// Same underlying /api/ai-safety/* endpoints and STARTERS; only the
// framing (title, hero icon, gate copy, gate accent, sample Qs)
// differs. Add a new mode by extending this map — no route branching
// elsewhere in the component is needed.
// ============================================================
const SAFETY_QS = [
  { icon: MessageCircle,  q: 'Is this message a scam?' },
  { icon: Flag,           q: 'Is this behavior a red flag?' },
  { icon: HelpCircle,     q: 'Should I meet this person?' },
  { icon: CalendarClock,  q: 'How do I plan a safe first date?' },
  { icon: ShieldHalf,     q: 'What are my options if I feel unsafe?' },
];

const DM_QS = [
  { icon: Sparkles,       q: 'Write me a great opener for this profile.' },
  { icon: MessageCircle,  q: "I'm mid-chat — help me reply." },
  { icon: Heart,          q: 'Am I reading the chemistry right?' },
  { icon: Type,           q: 'Rewrite my last message with a warmer tone.' },
  { icon: Coffee,         q: 'How do I ask them out without being awkward?' },
];

const MODES = {
  safety: {
    routeTitle: 'AI Safety Assistant',
    heroTitle:  'AI Safety Assistant',
    heroIcon:   Sparkles,
    subtitle:   'A private, non-judgmental helper for messy situations.',
    backTo:     '/safety',
    inputPlaceholder: 'Ask about a scam, red flag, or safety plan…',
    sampleQs:   SAFETY_QS,
    testIdPrefix: 'ai-chat',
    // Gate — purple gradient
    gateAccent: '#7C3AED',
    gateAccentDeep: '#4C1D95',
    gateIcon:   Sparkles,
    gateHeading: 'A VIP-only perk',
    gateBody:
      'The AI Safety Assistant is a private helper for safety scenarios — scam checks, red-flag reads, catfish detection, and safe-date planning. Upgrade to VIP to unlock it.',
    // Cross-link footer to the DM Assistant
    crosslink: {
      href: '/ai-coach',
      label: 'Need help with a message?',
      cta: 'Open the AI DM Assistant',
    },
  },
  dm: {
    routeTitle: 'AI DM Assistant',
    heroTitle:  'AI DM Assistant',
    heroIcon:   Wand2,
    subtitle:   'Openers, replies, chemistry reads, tone rewrites — so every message lands.',
    backTo:     '/messages',
    inputPlaceholder: 'Paste a chat, ask for an opener, or a rewrite…',
    sampleQs:   DM_QS,
    testIdPrefix: 'ai-dm',
    // Gate — blue gradient
    gateAccent: '#2563EB',
    gateAccentDeep: '#1E3A8A',
    gateIcon:   Wand2,
    gateHeading: 'A VIP-only perk',
    gateBody:
      'The AI Assistant crafts openers, tunes your replies, reads chemistry, and rewrites tone — so every message lands. Upgrade to VIP to unlock it.',
    // Cross-link footer to the Safety Assistant
    crosslink: {
      href: '/safety/ai-assistant',
      label: 'For safety scenarios,',
      cta: 'open the AI Safety Assistant',
    },
  },
};

/** Canned starter prompts keyed by `?starter=<key>` on route entry.
 *  When a matching key is present, the assistant opens straight into
 *  the transcript with this prompt auto-sent — no landing page shown.
 *  Same map is used by BOTH modes (safety + dm). */
const STARTERS = {
  opener_coach:
    "My reply rate on opening messages is low. Coach me on writing better first messages that feel human, curious, and personal — with 3 concrete examples I can adapt. Ask me one clarifying question about my dating style first.",
  chat_reply_help:
    "I'm mid-conversation with a match and want to send a great next message. Help me craft one that keeps the chat moving without being generic. Ask me one quick question about what they last said and what I'd like the vibe to be, then give me 3 tailored options to pick from.",
};

const PRINCIPLES = [
  { icon: Lock,          title: 'Private by default',           body: "Conversations aren't linked to your dating profile." },
  { icon: HandMetal,     title: 'Guidance, not verdicts',       body: 'It points to warning signs and options — not judgments about people.' },
  { icon: AlertCircle,   title: 'Always escalates',             body: 'If danger is mentioned, it points you to emergency resources first.' },
  { icon: GraduationCap, title: 'Trained on safety literature', body: 'Grounded in scam-prevention and relationship-safety research.' },
];

const SESSION_KEY = 'ai_safety_session_id';

export default function AISafetyChat({ mode = 'safety' }) {
  const M = MODES[mode] || MODES.safety;
  const isDm = mode === 'dm';
  const HeroIcon = M.heroIcon;
  const GateIcon = M.gateIcon;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_KEY) || '');
  const scrollerRef = useRef(null);
  const abortRef = useRef(null);
  // VIP preflight — mirrors the AI Date Planner gate. `null` = still loading,
  // `true` = VIP (render chat), `false` = not VIP (render gate).
  const [vipAllowed, setVipAllowed] = useState(null);
  const [tier, setTier] = useState('free');

  useEffect(() => {
    let cancel = false;
    const featureSlug = isDm ? 'ai_dm' : 'ai_safety';
    api.get('/ai-safety/access', { params: { feature: featureSlug } }).then((r) => {
      if (cancel) return;
      setVipAllowed(!!r.data?.allowed);
      setTier(r.data?.tier || 'free');
    }).catch(() => { if (!cancel) setVipAllowed(false); });
    return () => { cancel = true; };
  }, [isDm]);

  // Auto-scroll to the newest message when a delta lands.
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  // Canned starter prompt from `?starter=<key>` (e.g. Reply-Rate coaching
  // nudge on the Profile tab). Fires once — we strip the query param after
  // auto-send so a refresh doesn't re-fire it.
  const starterKey = searchParams.get('starter');
  useEffect(() => {
    if (!starterKey) return;
    // Don't auto-fire a chat message until we know the user is VIP —
    // otherwise the assistant placeholder would flash + toast a 403.
    if (vipAllowed !== true) return;
    const prompt = STARTERS[starterKey];
    if (!prompt) return;
    // Defer to next tick so the message list ref + streaming state are ready.
    const t = setTimeout(() => {
      send(prompt);
      setSearchParams((sp) => { sp.delete('starter'); return sp; }, { replace: true });
    }, 40);
    return () => clearTimeout(t);
  }, [starterKey, vipAllowed]);

  const backendBase = useMemo(() => {
    // api.defaults.baseURL is set to `${REACT_APP_BACKEND_URL}/api` in AuthContext.
    return (api?.defaults?.baseURL || '') + '/ai-safety/chat';
  }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput('');

    // Optimistic user turn + placeholder assistant turn we'll stream into.
    setMessages((m) => [
      ...m,
      { role: 'user', content: msg },
      { role: 'assistant', content: '', streaming: true },
    ]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(backendBase, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`,
        },
        body: JSON.stringify({ message: msg, session_id: sessionId || null, feature: isDm ? 'ai_dm' : 'ai_safety' }),
        signal: controller.signal,
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error(`Chat failed (${res.status})`);
      }

      // Server sends the new session id back via a header the first time.
      const newSid = res.headers.get('X-Session-Id');
      if (newSid && newSid !== sessionId) {
        setSessionId(newSid);
        sessionStorage.setItem(SESSION_KEY, newSid);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantText = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE events split by blank line.
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const event = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!event.startsWith('data:')) continue;
          const payload = event.slice(5).trim();
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              assistantText += obj.delta;
              setMessages((m) => {
                const next = [...m];
                const last = next[next.length - 1];
                if (last && last.role === 'assistant') {
                  next[next.length - 1] = { ...last, content: assistantText };
                }
                return next;
              });
            } else if (obj.error) {
              throw new Error(obj.error);
            }
          } catch (e) { /* ignore malformed chunks */ }
        }
      }
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') {
          next[next.length - 1] = { ...last, streaming: false };
        }
        return next;
      });
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error(e.message || 'Chat failed');
        // Remove the empty assistant placeholder on error.
        setMessages((m) => (m.length && m[m.length - 1].role === 'assistant' && !m[m.length - 1].content ? m.slice(0, -1) : m));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const clearChat = async () => {
    if (streaming && abortRef.current) abortRef.current.abort();
    if (sessionId) {
      try { await api.delete(`/ai-safety/sessions/${sessionId}`); } catch (e) { /* ignore */ }
    }
    sessionStorage.removeItem(SESSION_KEY);
    setSessionId('');
    setMessages([]);
    toast.success('Chat cleared');
  };

  const isEmpty = messages.length === 0;

  // ---- VIP gate ----
  // Show a full-page upsell while the preflight is loading (skeleton)
  // or when the user is not VIP. Mirrors the AI Date Planner gate copy
  // so both AI features have consistent monetization treatment.
  if (vipAllowed === null) {
    return (
      <LegalPageShell title={M.routeTitle} backTo={M.backTo}>
        <HeroCard icon={HeroIcon} title={M.heroTitle} />
        <div className="flex items-center justify-center py-12" data-testid={`${M.testIdPrefix}-preflight`}>
          <Loader2 size={22} className="animate-spin" style={{ color: M.gateAccent }}/>
        </div>
      </LegalPageShell>
    );
  }
  if (vipAllowed === false) {
    const gateBgAccent  = `${M.gateAccent}26`;      // ~15% alpha
    const gateBgDeep    = `${M.gateAccentDeep}26`;
    const gateRing      = `${M.gateAccent}26`;
    return (
      <LegalPageShell title={M.routeTitle} backTo={M.backTo}>
        <HeroCard icon={HeroIcon} title={M.heroTitle} />
        <div
          className="rounded-2xl bg-white p-6 text-center"
          style={{ border: `1px solid ${gateRing}` }}
          data-testid={`${M.testIdPrefix}-vip-gate`}
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ background: `linear-gradient(135deg, ${gateBgAccent}, ${gateBgDeep})` }}
          >
            <Crown size={22} style={{ color: M.gateAccent }}/>
          </div>
          <h4 className="text-[17px] font-[800] tracking-[-0.3px] text-[#0F0F10] mb-1.5">
            {M.gateHeading}
          </h4>
          <p className="text-[13px] text-[#6B7280] leading-relaxed mb-5 max-w-[300px] mx-auto">
            {M.gateBody}
          </p>
          <button
            onClick={() => navigate('/subscription')}
            data-testid={`${M.testIdPrefix}-upgrade-btn`}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-white text-[14px] font-[800] tracking-[-0.2px] transition-transform hover:scale-[1.01] active:scale-[0.99]"
            style={{
              background: `linear-gradient(135deg, ${M.gateAccent}, ${M.gateAccentDeep})`,
              boxShadow: `0 6px 18px -6px ${M.gateAccent}8C`,  // 55% alpha
            }}
          >
            Upgrade to VIP <ChevronRight size={16} />
          </button>
          <p className="text-[11px] text-[#8A8A93] mt-3">
            Current plan: <span className="font-[700] text-[#0F0F10] capitalize">{tier}</span>
          </p>
        </div>

        {/* Keep the principles visible so free users see the value prop before upgrading. */}
        <p className="text-[11.5px] font-[900] uppercase mt-6 mb-3" style={{ color: '#6B7280', letterSpacing: '0.8px' }}>
          What you&rsquo;d unlock
        </p>
        <div
          className="rounded-2xl p-3.5 bg-white space-y-3.5"
          style={{ border: '1px solid rgba(15,15,16,0.06)' }}
          data-testid={`${M.testIdPrefix}-principles-gated`}
        >
          {PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="flex items-start gap-3">
                <span className="w-[30px] h-[30px] rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#0F0F10' }}>
                  <Icon size={14} className="text-white" strokeWidth={2}/>
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] font-[900] text-[#0F0F10]">{p.title}</p>
                  <p className="text-[12px] text-[#6B7280]" style={{ lineHeight: '16px' }}>{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Cross-link footer — nudges users toward the OTHER AI surface. */}
        <button
          onClick={() => navigate(M.crosslink.href)}
          data-testid={`${M.testIdPrefix}-crosslink`}
          className="w-full mt-4 rounded-2xl bg-white p-3 flex items-center gap-2 text-left hover:bg-[#FAFAFB] transition-colors"
          style={{ border: '1px solid rgba(15,15,16,0.06)' }}
        >
          <span className="text-[12.5px] text-[#4B4B52] flex-1 leading-snug">
            <span className="text-[#0F0F10] font-[600]">{M.crosslink.label}</span>{' '}
            <span className="text-[#0F0F10] underline underline-offset-2 font-[700]">{M.crosslink.cta}</span>
          </span>
          <ChevronRight size={16} className="text-[#8A8A93] flex-shrink-0"/>
        </button>
        <LegalFooter />
      </LegalPageShell>
    );
  }

  return (
    <LegalPageShell title={M.routeTitle} backTo={M.backTo}>
      <HeroCard icon={HeroIcon} title={M.heroTitle} />
      <p className="text-center text-[13px] font-[600] text-[#6B7280] -mt-4 mb-4">
        {M.subtitle}
      </p>

      {/* Chat transcript */}
      {!isEmpty && (
        <div
          ref={scrollerRef}
          data-testid={`${M.testIdPrefix}-transcript`}
          className="mb-3 max-h-[420px] overflow-y-auto rounded-2xl bg-white space-y-3 p-3"
          style={{ border: `1px solid ${M.gateAccent}26` }}
        >
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`ai-msg-${m.role}-${i}`}
              className={`max-w-[86%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-[20px] whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'ml-auto text-white'
                  : 'mr-auto text-[#0F0F10]'
              }`}
              style={
                m.role === 'user'
                  ? { background: 'linear-gradient(135deg, #7E22CE, #1E4FD8)' }
                  : { background: '#F4F4F5' }
              }
            >
              {m.content}
              {m.streaming && !m.content && (
                <Loader2 size={14} className="animate-spin inline-block" style={{ color: M.gateAccent }}/>
              )}
              {m.streaming && m.content && (
                <span className="ml-1 inline-block w-1.5 h-3 align-middle animate-pulse" style={{ background: M.gateAccent }}/>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Input row */}
      <form
        data-testid={`${M.testIdPrefix}-form`}
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="flex items-end gap-2 mb-4"
      >
        <textarea
          data-testid={`${M.testIdPrefix}-input`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={M.inputPlaceholder}
          rows={1}
          maxLength={4000}
          disabled={streaming}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          className="flex-1 resize-none rounded-2xl px-4 py-3 text-[14px] text-[#0F0F10] focus:outline-none focus:ring-2 transition-all"
          style={{ background: '#FFFFFF', border: '1px solid rgba(15,15,16,0.1)', maxHeight: 120, '--tw-ring-color': `${M.gateAccent}4D` }}
        />
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          data-testid={`${M.testIdPrefix}-send`}
          className="w-11 h-11 rounded-full flex items-center justify-center text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
          style={{
            background: `linear-gradient(135deg, ${M.gateAccent}, ${M.gateAccentDeep})`,
            boxShadow: `0 4px 12px -2px ${M.gateAccent}66`,
          }}
          aria-label="Send"
        >
          {streaming ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
        </button>
      </form>

      {!isEmpty && (
        <div className="flex justify-end -mt-2 mb-4">
          <button
            onClick={clearChat}
            data-testid={`${M.testIdPrefix}-clear`}
            className="inline-flex items-center gap-1.5 text-[12px] font-[700] text-[#6B7280] hover:text-[#0F0F10]"
          >
            <Trash2 size={12} /> Clear chat
          </button>
        </div>
      )}

      {/* Empty-state onboarding — mode-specific sample questions + principles */}
      {isEmpty && (
        <>
          <p className="text-[11.5px] font-[900] uppercase mt-4 mb-3" style={{ color: '#6B7280', letterSpacing: '0.8px' }}>
            Try asking
          </p>
          <div className="space-y-2.5" data-testid={`${M.testIdPrefix}-sample-questions`}>
            {M.sampleQs.map((r, i) => {
              const Icon = r.icon;
              return (
                <button
                  key={i}
                  onClick={() => send(r.q)}
                  disabled={streaming}
                  data-testid={`${M.testIdPrefix}-sample-${i}`}
                  className="w-full flex items-start gap-2.5 rounded-xl p-3 bg-white text-left hover:shadow-[0_2px_8px_rgba(0,0,0,0.05)] transition-shadow disabled:opacity-60"
                  style={{ border: `1px solid ${M.gateAccent}26` }}
                >
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${M.gateAccent}1A` }}
                  >
                    <Icon size={15} style={{ color: M.gateAccent }} strokeWidth={2}/>
                  </span>
                  <span className="flex-1 min-w-0 text-[13.5px] font-[700] italic text-[#0F0F10]">&ldquo;{r.q}&rdquo;</span>
                </button>
              );
            })}
          </div>

          <p className="text-[11.5px] font-[900] uppercase mt-6 mb-3" style={{ color: '#6B7280', letterSpacing: '0.8px' }}>
            How it works
          </p>
          <div
            className="rounded-2xl p-3.5 bg-white space-y-3.5"
            style={{ border: '1px solid rgba(15,15,16,0.06)' }}
            data-testid={`${M.testIdPrefix}-principles`}
          >
            {PRINCIPLES.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-[30px] h-[30px] rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#0F0F10' }}>
                    <Icon size={14} className="text-white" strokeWidth={2}/>
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-[900] text-[#0F0F10]">{p.title}</p>
                    <p className="text-[12px] text-[#6B7280]" style={{ lineHeight: '16px' }}>{p.body}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4">
            <Callout tone="warning">AI can make mistakes and cannot determine another person&rsquo;s true intentions. Use it as a second opinion, never as a substitute for your own judgment or professional help.</Callout>
          </div>

          {/* Emergency link — safety mode only. The DM coach shouldn't
              nudge users to 911 while they're planning an opener. */}
          {!isDm && (
            <button
              onClick={() => navigate('/safety/emergency')}
              data-testid="ai-emergency-link"
              className="w-full flex items-center justify-center gap-2 rounded-full font-[700] text-[13px] py-3 mt-3"
              style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid rgba(220,38,38,0.2)' }}
            >
              <ShieldAlert size={14}/>
              In an emergency? See resources
            </button>
          )}

          {/* Cross-link to the OTHER AI surface so users can hop between
              the two modes without hunting through the menu. */}
          <button
            onClick={() => navigate(M.crosslink.href)}
            data-testid={`${M.testIdPrefix}-crosslink`}
            className="w-full mt-3 rounded-2xl bg-white p-3 flex items-center gap-2 text-left hover:bg-[#FAFAFB] transition-colors"
            style={{ border: '1px solid rgba(15,15,16,0.06)' }}
          >
            <span className="text-[12.5px] text-[#4B4B52] flex-1 leading-snug">
              <span className="text-[#0F0F10] font-[600]">{M.crosslink.label}</span>{' '}
              <span className="text-[#0F0F10] underline underline-offset-2 font-[700]">{M.crosslink.cta}</span>
            </span>
            <ChevronRight size={16} className="text-[#8A8A93] flex-shrink-0"/>
          </button>
        </>
      )}

      <LegalFooter />
    </LegalPageShell>
  );
}
